// ATE-CR-159 — typed API client for the verify-email-notifications admin
// endpoints in dashboard_api (see services/dashboard_api/src/.../api/
// verification_routes.py). The status endpoint can return a `Retry-After: 5`
// response header when the publish is recent (< 30s) and any stage is still
// pending; the dashboard uses that to schedule the next refresh, so this
// module exposes the third method's response wrapped in
// { data, retryAfterSeconds } rather than just the JSON body.
import { fetchApi, fetchApiWithResponse } from "@/api/client";

// ATE-CR-159
export const API_VERIFICATION_RECIPIENTS_PATH =
  "/admin/notifications/verification-recipients" as const;
// ATE-CR-159
export const API_VERIFICATIONS_PATH = "/admin/notifications/verifications" as const;

// ATE-CR-159 — single recipient row in the helm-managed allowlist.
export interface VerificationRecipient {
  readonly email: string;
  readonly label: string;
}

// ATE-CR-159 — GET /admin/notifications/verification-recipients response.
export interface VerificationRecipientsResponse {
  readonly recipients: readonly VerificationRecipient[];
}

// ATE-CR-159 — POST body for /admin/notifications/verifications.
export interface StartVerificationRequest {
  readonly recipient: string;
}

// ATE-CR-159 — POST response (202 Accepted).
export interface StartVerificationResponse {
  readonly verification_id: string;
  readonly trace_id: string;
  readonly recipient: string;
  readonly published_at: string; // ISO8601
}

// ATE-CR-159 — canonical 5-stage pipeline names. Mirrors the backend
// VerificationStage.name Literal in verification_schemas.py.
export type VerificationStageName =
  | "published"
  | "consumed"
  | "phase_1_persist"
  | "phase_2_email"
  | "graph_api_send";

// ATE-CR-159 — per-stage status. Mirrors backend VerificationStage.status
// Literal in verification_schemas.py.
export type VerificationStageStatus =
  | "pending"
  | "running"
  | "success"
  | "failed";

// ATE-CR-159 — one entry in the pipeline timeline.
export interface VerificationStage {
  readonly name: VerificationStageName;
  readonly status: VerificationStageStatus;
  readonly at: string | null;
  readonly duration_ms: number | null;
  readonly error: string | null;
}

// ATE-CR-159 — GET /admin/notifications/verifications/{id} body.
export interface VerificationStatusResponse {
  readonly verification_id: string;
  readonly trace_id: string;
  readonly recipient: string;
  readonly published_at: string;
  readonly stages: readonly VerificationStage[];
  readonly trace_console_url: string;
  readonly last_seen_parent: VerificationStageName | null;
  readonly trace_expired: boolean;
}

// ATE-CR-159
export async function listVerificationRecipients(
  signal?: AbortSignal,
): Promise<VerificationRecipientsResponse> {
  return fetchApi<VerificationRecipientsResponse>(
    API_VERIFICATION_RECIPIENTS_PATH,
    { signal },
  );
}

// ATE-CR-159
export async function startVerification(
  body: StartVerificationRequest,
  signal?: AbortSignal,
): Promise<StartVerificationResponse> {
  return fetchApi<StartVerificationResponse>(API_VERIFICATIONS_PATH, {
    method: "POST",
    body: JSON.stringify(body),
    signal,
  });
}

// ATE-CR-159 — also exposes the `Retry-After` header so the page can pace
// auto-refresh. Returns null when the header is absent or non-numeric.
export async function getVerificationStatus(
  verificationId: string,
  signal?: AbortSignal,
): Promise<{
  data: VerificationStatusResponse;
  retryAfterSeconds: number | null;
}> {
  const { data, response } = await fetchApiWithResponse<VerificationStatusResponse>(
    `${API_VERIFICATIONS_PATH}/${encodeURIComponent(verificationId)}`,
    { signal },
  );
  const raw = response.headers.get("retry-after");
  const parsed = raw === null ? NaN : Number.parseInt(raw, 10);
  const retryAfterSeconds = Number.isFinite(parsed) ? parsed : null;
  return { data, retryAfterSeconds };
}
